/**
 * Generic deep diff between two JSON snapshots of the same document.
 *
 * Audit history stores whole documents rather than per-field changes, so the
 * only way to describe a revision is to compare it against the snapshot that
 * replaced it. This flattens that comparison into one row per changed leaf.
 */

export type DiffKind = 'added' | 'removed' | 'changed'

export interface FieldDiff {
  /** Machine path into the object, e.g. "partners[0].tenant_name". */
  path: string
  /** Display path, e.g. "Partners 1 › Tenant Name". */
  label: string
  kind: DiffKind
  before: unknown
  after: unknown
}

export interface DiffOptions {
  /**
   * Keys to skip, matched against either the leaf key ('goid') or the full
   * path ('partners[0].goid').
   */
  ignore?: string[]
  /**
   * Compare only keys present on both sides. Use when `after` is a partial
   * projection of `before` (an API summary vs. the full stored document),
   * where an absent key means "not returned" rather than "removed".
   */
  sharedKeysOnly?: boolean
}

interface Cursor {
  path: string
  label: string
}

const ROOT: Cursor = { path: '', label: '' }

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Turns a snake_case document key into a display label.
 *
 * Examples:
 *   "pull_site_mrds" -> "Pull Site Mrds"
 *   "name" -> "Name"
 */
function humanizeKey(key: string): string {
  return key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function descendKey(cursor: Cursor, key: string): Cursor {
  const label = humanizeKey(key)
  return {
    path: cursor.path ? `${cursor.path}.${key}` : key,
    label: cursor.label ? `${cursor.label} › ${label}` : label,
  }
}

/** List positions are 1-based in the label so they read naturally. */
function descendIndex(cursor: Cursor, index: number): Cursor {
  return {
    path: `${cursor.path}[${index}]`,
    label: `${cursor.label} ${index + 1}`,
  }
}

function isIgnored(cursor: Cursor, options: DiffOptions): boolean {
  if (!cursor.path || !options.ignore?.length) return false
  const leaf = cursor.path.split('.').pop() ?? ''
  return options.ignore.includes(cursor.path) || options.ignore.includes(leaf)
}

function keysToCompare(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  options: DiffOptions,
): string[] {
  if (options.sharedKeysOnly) {
    return Object.keys(before).filter((key) => key in after)
  }
  return [...new Set([...Object.keys(before), ...Object.keys(after)])]
}

function compare(
  before: unknown,
  after: unknown,
  cursor: Cursor,
  options: DiffOptions,
  out: FieldDiff[],
): void {
  if (isIgnored(cursor, options)) return

  if (isPlainObject(before) && isPlainObject(after)) {
    for (const key of keysToCompare(before, after, options)) {
      compare(before[key], after[key], descendKey(cursor, key), options, out)
    }
    return
  }

  if (Array.isArray(before) && Array.isArray(after)) {
    for (let i = 0; i < Math.max(before.length, after.length); i++) {
      compare(before[i], after[i], descendIndex(cursor, i), options, out)
    }
    return
  }

  // Anything left is a leaf: two primitives, or a structure replaced by a
  // value of a different shape.
  if (Object.is(before, after)) return

  out.push({
    path: cursor.path,
    label: cursor.label,
    kind:
      before === undefined
        ? 'added'
        : after === undefined
          ? 'removed'
          : 'changed',
    before,
    after,
  })
}

/**
 * Flattens the differences between two documents into one entry per changed
 * leaf, in the order the keys appear.
 *
 * Examples:
 *   diffObjects({ name: 'A' }, { name: 'B' })
 *     -> [{ path: 'name', label: 'Name', kind: 'changed', before: 'A', after: 'B' }]
 */
export function diffObjects(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
  options: DiffOptions = {},
): FieldDiff[] {
  const diffs: FieldDiff[] = []
  compare(before ?? {}, after ?? {}, ROOT, options, diffs)
  return diffs
}

/**
 * Renders a diffed value for display.
 *
 * Examples:
 *   (undefined) -> "—"   (absent on this side)
 *   ("") -> '""'
 *   ({ a: 1 }) -> '{"a":1}'
 */
export function formatDiffValue(value: unknown): string {
  if (value === undefined) return '—'
  if (value === null) return 'null'
  if (typeof value === 'string') return value === '' ? '""' : value
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return JSON.stringify(value) ?? String(value)
}
